import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SectionWrapper from '../shared/SectionWrapper';
import AnimatedCounter from '../shared/AnimatedCounter';

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */
const frameworks = [
  { code: 'GRI',          name: 'Global Reporting Initiative',                  description: 'The most widely used sustainability reporting standards, covering economic, environmental, and social impacts.', accent: '#16a34a' },
  { code: 'SASB',         name: 'Sustainability Accounting Standards',           description: 'Industry-specific disclosures connecting sustainability performance to financial outcomes for investors.',          accent: '#2563eb' },
  { code: 'TCFD',         name: 'Climate Financial Disclosures',                 description: 'Recommendations for climate-related financial risk disclosures across governance, strategy, and metrics.',           accent: '#0891b2' },
  { code: 'CSRD / ESRS',  name: 'EU Corporate Sustainability Reporting',         description: 'EU mandatory reporting with detailed European standards for double materiality assessment.',                         accent: '#7c3aed' },
  { code: 'SEBI BRSR',    name: 'India Business Responsibility',                 description: 'Mandatory ESG disclosures for top 1,000 listed companies in India. India-specific and regulator-backed.',            accent: '#ea580c', badge: 'India-First' },
  { code: 'CDP',          name: 'Climate Disclosure Project',                    description: 'Global disclosure system managing environmental impacts on climate, water, and forests for companies worldwide.',     accent: '#0d9488' },
  { code: 'ISSB / IFRS',  name: 'International Sustainability Standards',        description: 'Global baseline disclosure standards issued by the ISSB for capital markets worldwide.',                             accent: '#1d4ed8', badge: 'New' },
  { code: 'EU Taxonomy',  name: 'EU Classification System',                      description: 'Classification system for environmentally sustainable economic activities across the European Union.',                accent: '#4f46e5' },
  { code: 'TNFD',         name: 'Nature-related Financial Disclosures',          description: 'Framework for reporting on nature-related dependencies, impacts, risks, and opportunities.',                          accent: '#059669' },
  { code: 'GHG Protocol', name: 'Greenhouse Gas Protocol',                       description: 'The most widely used accounting tool for quantifying and managing Scope 1, 2, and 3 GHG emissions.',                  accent: '#dc2626' },
  { code: 'UN SDGs',      name: 'UN Sustainable Development Goals',              description: '17 global goals adopted by all UN member states — a shared blueprint for people and the planet by 2030.',            accent: '#0284c7' },
  { code: 'SFDR',         name: 'Sustainable Finance Disclosure Regulation',     description: 'EU regulation requiring financial market participants to disclose sustainability risks of investment decisions.',       accent: '#9333ea' },
];

const stats = [
  { value: 35,   suffix: '+',  label: 'Frameworks' },
  { value: 12,   suffix: '+',  label: 'Regions' },
  { value: 99.8, suffix: '%',  label: 'Accuracy' },
  { value: 100,  suffix: '%',  label: 'Compliance' },
];

/* ------------------------------------------------------------------ */
/*  Flip card                                                          */
/* ------------------------------------------------------------------ */
const FlipCard = ({ fw, i }) => {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      className="relative cursor-default"
      style={{ perspective: 700 }}
      onHoverStart={() => setOpen(true)}
      onHoverEnd={() => setOpen(false)}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        style={{ transformStyle: 'preserve-3d', transformOrigin: 'center' }}
        animate={{ rotateY: open ? 180 : 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full"
      >
        {/* ── Front face ── */}
        <div
          className="rounded-xl bg-white dark:bg-slate-800 flex flex-col items-center justify-center gap-1.5 px-3 py-5 select-none"
          style={{
            backfaceVisibility: 'hidden',
            minHeight: 110,
            border: `0.75px solid rgba(0,0,0,0.75)`,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          }}
        >
          <span
            className="text-base font-extrabold tracking-tight text-center leading-tight"
            style={{
              background: `linear-gradient(135deg, ${fw.accent} 0%, ${fw.accent}99 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {fw.code}
          </span>
          {fw.badge && (
            <span className="text-[0.5rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: fw.accent + '18', color: fw.accent }}>
              {fw.badge}
            </span>
          )}
        </div>

        {/* ── Back face ── */}
        <div
          className="absolute inset-0 rounded-xl px-4 py-4 flex flex-col justify-between"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            background: fw.accent + '14',
            border: `0.75px solid ${fw.accent}40`,
            minHeight: 110,
          }}
        >
          <div>
            <div className="text-[0.65rem] font-black uppercase tracking-wider" style={{ color: fw.accent }}>{fw.code}</div>
            <div className="mt-1 text-[0.7rem] font-bold text-gray-800 dark:text-white leading-snug">{fw.name}</div>
          </div>
          <p className="text-[0.6rem] text-gray-500 dark:text-slate-400 leading-relaxed mt-2">{fw.description}</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */
const FrameworksSection = () => (
  <SectionWrapper
    id="frameworks"
    bgClass="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-[#0a0f1a] dark:to-[#0f172a]"
    padding="py-10 md:py-12"
  >
    {/* Header */}
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <h2
        className="font-extrabold tracking-tight text-gray-900 dark:text-white leading-tight"
        style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}
      >
        35+ ESG Frameworks,{' '}
        <span className="bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">
          One Platform
        </span>
      </h2>
      <p className="text-sm text-gray-500 dark:text-slate-400 sm:text-right sm:max-w-xs flex-shrink-0">
        Hover any framework to learn more. Automatic mapping across every jurisdiction.
      </p>
    </div>

    {/* Flipbook grid */}
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
      {frameworks.map((fw, i) => (
        <FlipCard key={fw.code} fw={fw} i={i} />
      ))}
    </div>

    {/* Stats row */}
    <motion.div
      className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 rounded-2xl border border-white/60 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 backdrop-blur-sm px-6 py-6 shadow-sm"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
    >
      {stats.map((stat, idx) => (
        <div key={idx} className="text-center">
          <div className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">
            <AnimatedCounter target={stat.value} suffix={stat.suffix} duration={2000} />
          </div>
          <div className="mt-1 text-xs font-medium uppercase tracking-widest text-gray-400 dark:text-slate-500">
            {stat.label}
          </div>
        </div>
      ))}
    </motion.div>
  </SectionWrapper>
);

export default FrameworksSection;
